import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../api";
import {
  GoogleMap,
  LoadScript,
  Marker,
  Polyline,
  InfoWindow,
  Circle,
} from "@react-google-maps/api";
import { decode } from "@googlemaps/polyline-codec";
import { closeConnection, SignalRImpl } from "../../SignalRImpl";
import "../../css_files/Technician/ViewLocation.css";
import { toast } from "react-toastify";

function ViewLocation() {
  const { id } = useParams();
  const [locationData, setLocationData] = useState(null);
  const [polylineArr, setPolylineArr] = useState([]);
  const ws = useRef(null);
  const [nearbyloading, setnearbyloading] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [coordinates, setCoordinates] = useState(null);
  const locationSubscription = useRef(null);
  const [nearbyLocations, setNearbyLocations] = useState([]);
  const [destinationAddress, setDestinationAddress] = useState("");
  const [destinationDistance, setDestinationDistance] = useState(0.0);
  const [refinedNearbyLocations, setRefinedNearbyLocations] = useState([]);
  const [shownearbyinfo, setshownearbyinfo] = useState([]);
  const [nearbyinfoindex, setnearbyinfoindex] = useState(0);

  useEffect(() => {
    const connection = SignalRImpl();

    ws.current = connection;
    connection
      .start()
      .then(() => {
        connection.on("ReceiveLocation", (msg) => {
          const message = JSON.parse(msg);
          setLocationData({
            ...locationData,
            fromLocationLongitude: message?.longitude,
            fromLocationLatitude: message?.latitude,
          });
        });
      })
      .catch(console.log);
    return () => {
      closeConnection();
    };
  }, []);

  useEffect(() => {
    axiosInstance
      .get(
        `${process.env.REACT_APP_API_URL}/api/LocationHistory/GetSingleLocationHistory/${id}`
      )
      .then((res) => {
        setLocationData(res.data);

        const address = res?.data?.ticket?.newEnquiry?.isAddressSame
          ? ` ${res?.data?.ticket?.newEnquiry?.billToAddress?.address1}, ${
              res?.data?.ticket?.newEnquiry?.billToAddress?.address2
            }${res?.data?.ticket?.newEnquiry?.billToAddress?.address2 && ", "}${
              res?.data?.ticket?.newEnquiry?.billToAddress?.cityObj?.name ??
              res?.data?.ticket?.newEnquiry?.billToAddress?.cityName
            }, ${
              res?.data?.ticket?.newEnquiry?.billToAddress?.stateObj?.name ??
              res?.data?.ticket?.newEnquiry?.billToAddress?.stateName
            }, ${res?.data?.ticket?.newEnquiry?.billToAddress?.country}, ${
              res?.data?.ticket?.newEnquiry?.billToAddress?.pincode
            }`
          : `${res?.data?.ticket?.newEnquiry?.shipToAddress?.address1}, ${
              res?.data?.ticket?.newEnquiry?.shipToAddress?.address2
            }${res?.data?.ticket?.newEnquiry?.shipToAddress?.address2 && ", "}${
              res?.data?.ticket?.newEnquiry?.shipToAddress?.cityObj?.name ??
              res?.data?.ticket?.newEnquiry?.shipToAddress?.cityName
            }, ${
              res?.data?.ticket?.newEnquiry?.shipToAddress?.stateObj?.name ??
              res?.data?.ticket?.newEnquiry?.shipToAddress?.stateName
            }, ${res?.data?.ticket?.newEnquiry?.shipToAddress?.country}, ${
              res?.data?.ticket?.newEnquiry?.shipToAddress?.pincode
            }`;
        setDestinationAddress(address);
        const payload = {
          origin: {
            location: {
              latLng: {
                latitude: res.data?.fromLocationLatitude,
                longitude: res.data?.fromLocationLongitude,
              },
            },
          },
          destination: {
            location: {
              latLng: {
                latitude: res.data?.toLocationLatitude,
                longitude: res.data?.toLocationLongitude,
              },
            },
          },
          travelMode: "DRIVE",
          computeAlternativeRoutes: false,
          routeModifiers: {
            avoidTolls: false,
            avoidHighways: false,
            avoidFerries: false,
          },
          languageCode: "en-US",
          units: "METRIC",
          polylineQuality: "OVERVIEW",
          polylineEncoding: "ENCODED_POLYLINE",
        };

        axiosInstance
          .post(
            `${process.env.REACT_APP_API_URL}/api/LocationHistory/GetRoutes`,
            payload
          )
          .then(async (res) => {
            const encoded = res.data?.routes?.[0]?.polyline?.encodedPolyline;

            if (!encoded) {
              return;
            }

            const coordsArray = decode(encoded, 5);
            if (coordsArray) {
              locationTracking();
            }
            setPolylineArr(
              coordsArray.map(([lat, lng]) => ({
                lat,
                lng,
              }))
            );
          })
          .catch(console.log);
      })
      .catch(console.log);
  }, [id]);

  async function locationTracking() {
    // Prevent duplicate watchers
    if (locationSubscription.current !== null) return;

    // Check browser support
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by this browser.");
      return;
    }

    // Check secure context (HTTPS or localhost)
    if (!window.isSecureContext) {
      alert("Location access requires HTTPS or localhost.");
      return;
    }

    try {
      // Check permission if supported by the browser
      if (navigator.permissions?.query) {
        const permission = await navigator.permissions.query({
          name: "geolocation",
        });

        if (permission.state === "denied") {
          alert("Location permission denied. Enable it in browser settings.");
          return;
        }
      }

      // Prevent duplicate watchers after asynchronous checks
      if (locationSubscription.current !== null) return;

      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          const { latitude, longitude } = position.coords;

          setCoordinates({ latitude, longitude });
        },
        (error) => {
          console.error("Location tracking error:", error.message);

          if (error.code === error.PERMISSION_DENIED) {
            alert("Location permission is required.");
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            console.error("Location information is unavailable.");
          } else if (error.code === error.TIMEOUT) {
            console.error("Location request timed out.");
          }
        },
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 20000,
        }
      );

      locationSubscription.current = watchId;
    } catch (error) {
      console.error("Failed to start location tracking:", error);
    }
  }

  useEffect(() => {
    return () => {
      if (locationSubscription.current !== null) {
        navigator.geolocation.clearWatch(locationSubscription.current);
        locationSubscription.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (
      locationData?.toLocationLatitude &&
      locationData?.toLocationLongitude &&
      Object.keys(coordinates ?? {}).length > 0
    ) {
      axiosInstance
        .post(
          `${process.env.REACT_APP_API_URL}/api/LocationHistory/CalculateDistance`,
          {
            Latitude: coordinates.latitude,
            Longitude: coordinates.longitude,
            ToLatitude: locationData?.toLocationLatitude,
            ToLongitude: locationData?.toLocationLongitude,
          }
        )
        .then((res) => {
          setDestinationDistance(res?.data);
        })
        .catch(console.log);
    }
    if (
      nearbyLocations.length > 0 &&
      Object.keys(coordinates ?? {}).length > 0
    ) {
      Promise.all(
        nearbyLocations.map(async (x) => {
          const response = await axiosInstance.post(
            `${process.env.REACT_APP_API_URL}/api/LocationHistory/CalculateDistance`,
            {
              Latitude: coordinates.latitude,
              Longitude: coordinates.longitude,
              ToLatitude: x.latitude,
              ToLongitude: x.longitude,
            }
          );

          return {
            ...x,
            distance: response?.data,
          };
        })
      )
        .then((results) => {
          setRefinedNearbyLocations(results);
        })
        .catch(console.log);
    }
  }, [coordinates, nearbyLocations, locationData]);

  return (
    <LoadScript googleMapsApiKey={process.env.REACT_APP_MAPS_KEY}>
      <div className="view-location-div">
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: 800 }}
          center={{
            lat: coordinates?.latitude,
            lng: coordinates?.longitude,
          }}
          zoom={10}
        >
          <button
            className="view-location-button"
            disabled={
              nearbyloading || Object.keys(coordinates ?? {}).length === 0
            }
            onClick={() => {
              setnearbyloading(true);
              axiosInstance
                .post(
                  `${process.env.REACT_APP_API_URL}/api/LocationHistory/GetNearbyLocations`,
                  {
                    latitude: coordinates?.latitude,
                    longitude: coordinates?.longitude,
                  }
                )
                .then((res) => {
                  setNearbyLocations(res.data);
                  setshownearbyinfo(
                    Array.from({ length: res.data?.length }, () => false)
                  );
                  toast.success("Nearby Locations Fetched Successfully", {
                    position: "bottom-center",
                    theme: "dark",
                  });
                })
                .catch(console.log)
                .finally(() => {
                  setnearbyloading(false);
                });
            }}
          >
            {nearbyloading || Object.keys(coordinates ?? {}).length === 0
              ? "Please Wait..."
              : "Show Nearby Locations"}
          </button>
          {coordinates && (
            <Marker
              position={{
                lat: coordinates?.latitude,
                lng: coordinates?.longitude,
              }}
              title="Your current location"
              icon={{
                url:
                  "data:image/svg+xml;charset=UTF-8," +
                  encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg"
               width="48" height="48" viewBox="0 0 48 48">
            <path d="M24 2 L10 38 L24 30 L38 38 Z"
                  fill="#4285F4" fill-opacity="0.28"/>
            <circle cx="24" cy="24" r="10"
                    fill="#4285F4" stroke="white" stroke-width="3"/>
            <circle cx="24" cy="24" r="4" fill="white"/>
          </svg>
        `),
                scaledSize: new window.google.maps.Size(48, 48),
                anchor: new window.google.maps.Point(24, 24),
              }}
            />
          )}

          <Marker
            position={{
              lat: locationData?.toLocationLatitude,
              lng: locationData?.toLocationLongitude,
            }}
            onClick={() => setShowInfo(true)}
          />
          {showInfo && (
            <InfoWindow
              position={{
                lat: locationData?.toLocationLatitude,
                lng: locationData?.toLocationLongitude,
              }}
              onCloseClick={() => setShowInfo(false)}
            >
              <div style={{ maxWidth: "250px" }}>
                <h3>Distance: {destinationDistance}km (Approx)</h3>
                <p>{destinationAddress}</p>
              </div>
            </InfoWindow>
          )}
          {shownearbyinfo[nearbyinfoindex] && (
            <InfoWindow
              position={{
                lat: refinedNearbyLocations[nearbyinfoindex]?.latitude,
                lng: refinedNearbyLocations[nearbyinfoindex]?.longitude,
              }}
              onCloseClick={() =>
                setshownearbyinfo((prev) => prev.map((_) => false))
              }
            >
              <div style={{ maxWidth: "250px" }}>
                <h3>
                  Distance: {refinedNearbyLocations[nearbyinfoindex]?.distance}
                  km (Approx)
                </h3>
                <p>{refinedNearbyLocations[nearbyinfoindex]?.address}</p>
              </div>
            </InfoWindow>
          )}
          <Marker
            position={{
              lat: locationData?.fromLocationLatitude,
              lng: locationData?.fromLocationLongitude,
            }}
            icon={{
              path: window?.google?.maps?.SymbolPath?.CIRCLE,
              scale: 9,
              fillColor: "brown",
              fillOpacity: 1,
              strokeColor: "#FFFFFF",
              strokeWeight: 3,
            }}
            zIndex={1000}
          />

          {refinedNearbyLocations?.map((x, i) => (
            <Marker
              position={{
                lat: x?.latitude,
                lng: x?.longitude,
              }}
              icon={{
                path: window?.google?.maps?.SymbolPath?.CIRCLE,
                scale: 9,
                fillColor: "green",
                fillOpacity: 1,
                strokeColor: "#FFFFFF",
                strokeWeight: 3,
              }}
              zIndex={1000}
              onClick={() => {
                setnearbyinfoindex(i);
                setshownearbyinfo((prev) => prev.map((_, j) => j === i));
              }}
            />
          ))}

          <Polyline
            path={polylineArr}
            options={{
              strokeColor: "#FF0000",
              strokeOpacity: 1.0,
              strokeWeight: 4,
            }}
          />
        </GoogleMap>
      </div>
    </LoadScript>
  );
}

export default ViewLocation;
